import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Upload, Camera, Save, Loader2, Sparkles, AlertCircle, ScanLine, ZoomIn } from 'lucide-react';
import { generateContentWithFallback } from '@/lib/geminiClient';
import { toast } from 'sonner';
import { useKurs } from '@/hooks/useKurs';

// Konstanta opsi mata uang
const CURRENCIES = ['IDR', 'USD', 'EUR'];

export default function BibitScannerModal({ onClose }) {
  const qc = useQueryClient();
  const { kurs, kursEuro } = useKurs();
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // State untuk form & tabel hasil scan
  const [vendorName, setVendorName] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  
  // Pengaturan Bulk (Global)
  const [globalPpnType, setGlobalPpnType] = useState('exclude');
  const [globalPpnPct, setGlobalPpnPct] = useState(11);
  const [globalCurrency, setGlobalCurrency] = useState('IDR');
  const [isDragging, setIsDragging] = useState(false);

  // Mengubah gambar file ke base64 (helper)
  const fileToGenerativePart = async (file) => {
    const base64EncodedDataPromise = new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(file);
    });
    return {
      inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
    };
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    processImageWithAI(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.type.startsWith('image/')) {
        toast.error('Mohon masukkan file berupa gambar (JPG/PNG)');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      
      processImageWithAI(file);
    }
  };

  const processImageWithAI = async (file) => {
    setIsProcessing(true);
    try {

      const prompt = `
        Kamu adalah sistem OCR ahli pengekstrak data invoice/quotation/pricelist/nota/struk pembelian bahan/bibit parfum.
        Tugasmu:
        1. Ekstrak nama Vendor/Supplier (biasanya di paling atas/logo, contoh: "TechnicoFlor", "Parfarome", "Luzi", dll).
        2. Ekstrak daftar barang/item dari tabel.
        3. Untuk setiap item, gabungkan KODE ITEM dan NAMA ITEM menjadi satu string di field "nama_bibit" (Contoh: "RT96359 - MINT TEA" atau "FR0123 - Baccarat Rouge"). Jika tidak ada kode, cukup tulis namanya saja.
        4. Ekstrak harganya dalam bentuk angka murni (tanpa titik koma) di field "harga". Jika harganya dalam USD/EUR, biarkan angkanya tapi set "mata_uang" ke "USD" atau "EUR". Jika rupiah, set "IDR".
        5. Ekstrak informasi tambahan yang penting (seperti Minimum Order Quantity / MOQ, Terms, atau syarat khusus) dan gabungkan ke dalam field "catatan". Jika tidak ada, kosongkan string "".
        
        Keluarkan data HANYA dalam format JSON valid seperti ini tanpa teks markdown lainnya:
        {
          "vendor": "Nama Vendor",
          "items": [
            { "nama_bibit": "FR0101 - Nama Bibit A", "harga": 1500000, "mata_uang": "IDR", "catatan": "MOQ 25 Kg" },
            { "nama_bibit": "Nama Bibit B", "harga": 25.5, "mata_uang": "USD", "catatan": "" }
          ]
        }
      `;

      const imagePart = await fileToGenerativePart(file);
      
      // Menggunakan fallback helper untuk memproses gambar
      const responseText = await generateContentWithFallback(
        undefined, // Tidak pakai system instruction khusus, gabung di prompt saja
        [prompt, imagePart],
        [],
        'gemini-3.5-flash',
        { generationConfig: { responseMimeType: 'application/json' } }
      );
      
      
      // Ekstrak JSON dari respon teks
      const firstBrace = responseText.indexOf('{');
      const lastBrace = responseText.lastIndexOf('}');
      if (firstBrace === -1 || lastBrace === -1) {
        throw new Error('Respon AI tidak berisi objek JSON valid');
      }
      const jsonString = responseText.substring(firstBrace, lastBrace + 1);
      const parsedData = JSON.parse(jsonString);

      setVendorName(parsedData.vendor || '');
      
      const itemsWithId = (parsedData.items || []).map((item, idx) => ({
        id: Date.now() + idx,
        nama_bibit: item.nama_bibit || '',
        harga: Number(item.harga) || 0,
        mata_uang: item.mata_uang || 'IDR',
        catatan: item.catatan || ''
      }));
      
      setScannedItems(itemsWithId);
      
      // Jika AI mendeteksi mayoritas mata uang tertentu, set global currency
      if (itemsWithId.length > 0) {
        setGlobalCurrency(itemsWithId[0].mata_uang || 'IDR');
      }

      toast.success('Berhasil mengekstrak data invoice!');
    } catch (error) {
      console.error('Error scanning invoice:', error);
      toast.error('Gagal memproses gambar. Pastikan gambar jelas atau cek kuota/model API Key.');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleItemChange = (id, field, value) => {
    setScannedItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (id) => {
    setScannedItems(prev => prev.filter(item => item.id !== id));
  };

  const saveMut = useMutation({
    mutationFn: async (itemsToSave) => {
      // Menyimpan data secara berurutan
      for (const item of itemsToSave) {
        await base44.entities.BibitSample.create(item);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bibit-sample'] });
      toast.success(`${scannedItems.length} bibit berhasil disimpan!`);
      onClose();
    },
    onError: (err) => {
      toast.error('Gagal menyimpan data: ' + err.message);
    }
  });

  const handleSaveAll = () => {
    if (!vendorName.trim()) {
      toast.error('Nama Vendor wajib diisi!');
      return;
    }
    
    if (scannedItems.length === 0) {
      toast.error('Tidak ada item untuk disimpan!');
      return;
    }

    const itemsToSave = scannedItems.map(item => {
      let convertedHarga = item.harga;
      if (item.mata_uang === 'USD' && kurs) {
        convertedHarga = item.harga * kurs;
      } else if (item.mata_uang === 'EUR' && kursEuro) {
        convertedHarga = item.harga * kursEuro;
      }

      const payload = {
        vendor: vendorName,
        nama_bibit: item.nama_bibit,
        mata_uang: item.mata_uang,
        status_ketersediaan: 'Tersedia',
        ppn_type: globalPpnType,
        ppn_percentage: globalPpnPct,
        harga_sebelum_ppn: convertedHarga,
        harga: item.harga, // Menyimpan harga dasarnya (ini akan dihandle hook/form jika perlu)
        catatan: item.catatan
      };
      
      // Jika Parfarome, assign grade default jika diperlukan, tapi dikosongi saja agar bisa diedit nanti
      if (vendorName.toLowerCase().includes('parfarome')) {
        payload.grade = 'Premium'; // Default grade
      }
      
      return payload;
    });

    saveMut.mutate(itemsToSave);
  };

  const applyGlobalCurrency = () => {
    setScannedItems(prev => prev.map(item => ({ ...item, mata_uang: globalCurrency })));
    toast.success(`Semua item diset ke ${globalCurrency}`);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60  flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-6xl min-h-[500px] flex flex-col border border-border animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header Modal */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 text-indigo-700 p-2 rounded-xl">
              <ScanLine className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Auto-Scan Invoice Bibit</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Ekstrak vendor dan harga otomatis dari gambar dengan AI</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-xl hover:bg-rose-100 hover:text-rose-600 transition-colors">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {!imagePreview && !isProcessing ? (
            // State: Belum ada gambar
            <div 
              className={`flex flex-col items-center justify-center h-full min-h-[350px] border-2 border-dashed rounded-2xl transition-all cursor-pointer ${
                isDragging 
                  ? 'border-primary bg-primary/10 scale-[1.02]' 
                  : 'border-border bg-muted/10 hover:bg-muted/30 hover:border-primary/50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className={`p-5 rounded-full mb-4 transition-colors ${isDragging ? 'bg-primary/20 scale-110' : 'bg-primary/10'}`}>
                <Camera className={`w-10 h-10 ${isDragging ? 'text-primary' : 'text-primary/80'}`} />
              </div>
              <h4 className="text-lg font-bold mb-2">
                {isDragging ? 'Lepaskan Gambar Di Sini' : 'Upload, Foto, atau Drag & Drop Invoice'}
              </h4>
              <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
                Klik kotak ini, atau cukup tarik dan lepas (drag & drop) gambar struk dari foldermu ke sini.
              </p>
              <Button className="gap-2 rounded-xl px-6 pointer-events-none">
                <Upload className="w-4 h-4" /> Pilih File Gambar
              </Button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileUpload} 
                accept="image/*" 
                className="hidden" 
              />
            </div>
          ) : isProcessing ? (
            // State: Sedang diproses AI
            <div className="flex flex-col items-center justify-center h-full min-h-[350px]">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/20 blur-xl rounded-full animate-pulse"></div>
                <Loader2 className="w-16 h-16 text-primary animate-spin relative" />
              </div>
              <h4 className="text-lg font-bold mt-8 mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" /> AI Sedang Membaca Dokumen...
              </h4>
              <p className="text-sm text-muted-foreground">Mengekstrak teks, vendor, dan harga. Tunggu sebentar ya!</p>
            </div>
          ) : (
            // State: Review Hasil
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Kolom Kiri: Info Vendor & Gambar */}
                <div className="sm:w-1/3 space-y-4">
                  <div className="bg-muted/30 p-4 rounded-2xl border border-border">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 block">Nama Vendor Terdeteksi</label>
                    <Input 
                      value={vendorName} 
                      onChange={e => setVendorName(e.target.value)} 
                      placeholder="Masukkan nama vendor" 
                      className="font-bold text-lg h-12 bg-background border-primary/30 focus-visible:ring-primary"
                    />
                  </div>
                  
                  <div className="bg-muted/30 p-2 rounded-2xl border border-border">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2 pt-2 pb-1 block">Preview Gambar</label>
                    <div className="aspect-[3/4] relative rounded-xl overflow-hidden bg-black/5 border border-border group cursor-pointer" onClick={() => setIsFullscreen(true)}>
                      <img src={imagePreview} alt="Invoice Preview" className="object-cover w-full h-full group-hover:opacity-80 transition-opacity" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                        <div className="bg-black/50 text-white p-2 rounded-full">
                          <ZoomIn className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-2 gap-2 rounded-xl" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="w-3.5 h-3.5" /> Ganti Gambar
                    </Button>
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                  </div>
                </div>

                {/* Kolom Kanan: Tabel Hasil Scan */}
                <div className="sm:w-2/3 flex flex-col">
                  {/* Pengaturan Global PPN & Mata Uang */}
                  <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-blue-500" />
                      <h4 className="font-bold text-sm text-blue-900">Pengaturan Global (Untuk Semua Item)</h4>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="lg:col-span-2">
                        <label className="text-xs font-medium text-blue-800 mb-1 block">Status PPN Global</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setGlobalPpnType('include')}
                            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${globalPpnType === 'include' ? 'bg-emerald-500 text-white shadow-md' : 'bg-white border border-blue-200 text-slate-600 hover:border-emerald-300'}`}
                          >
                            Include PPN
                          </button>
                          <button
                            type="button"
                            onClick={() => setGlobalPpnType('exclude')}
                            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${globalPpnType === 'exclude' ? 'bg-amber-500 text-white shadow-md' : 'bg-white border border-blue-200 text-slate-600 hover:border-amber-300'}`}
                          >
                            Exclude PPN
                          </button>
                        </div>
                      </div>
                      
                      {globalPpnType === 'exclude' && (
                        <div>
                          <label className="text-xs font-medium text-blue-800 mb-1 block">Persentase PPN (%)</label>
                          <Input 
                            type="number" 
                            value={globalPpnPct} 
                            onChange={e => setGlobalPpnPct(Number(e.target.value))} 
                            className="h-9 rounded-xl bg-white border-blue-200 text-sm"
                          />
                        </div>
                      )}
                      
                      <div className={globalPpnType === 'include' ? 'lg:col-span-2 flex items-end gap-2' : 'flex items-end gap-2'}>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-blue-800 mb-1 block">Set Semua Mata Uang</label>
                          <Select value={globalCurrency} onValueChange={setGlobalCurrency}>
                            <SelectTrigger className="w-full h-9 rounded-xl border border-blue-200 bg-white text-sm focus:ring-0 focus:ring-offset-0">
                              <SelectValue placeholder="Mata Uang" />
                            </SelectTrigger>
                            <SelectContent className="z-[70]">
                              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={applyGlobalCurrency} variant="secondary" className="h-9 px-3 rounded-xl bg-blue-100 text-blue-700 hover:bg-blue-200 shrink-0">
                          Terapkan
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Tabel Items */}
                  <div className="border border-border rounded-2xl overflow-hidden flex-1 flex flex-col bg-card shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-muted-foreground uppercase bg-muted/40">
                          <tr>
                            <th className="px-4 py-3 font-semibold w-1/3">Kode & Nama Bibit</th>
                            <th className="px-4 py-3 font-semibold w-[120px]">Mata Uang</th>
                            <th className="px-4 py-3 font-semibold w-[120px]">Nominal Harga</th>
                            <th className="px-4 py-3 font-semibold">Catatan (MOQ dll)</th>
                            <th className="px-4 py-3 text-center w-12">Hapus</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {scannedItems.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                Tidak ada item yang terdeteksi.<br/>Silakan ketik manual atau foto ulang.
                              </td>
                            </tr>
                          ) : (
                            scannedItems.map((item, idx) => (
                              <tr key={item.id} className="hover:bg-muted/10 transition-colors">
                                <td className="px-4 py-2 align-top">
                                  <Input 
                                    value={item.nama_bibit} 
                                    onChange={e => handleItemChange(item.id, 'nama_bibit', e.target.value)} 
                                    className="h-9 rounded-lg font-medium"
                                  />
                                </td>
                                <td className="px-4 py-2 align-top">
                                  <Select value={item.mata_uang} onValueChange={val => handleItemChange(item.id, 'mata_uang', val)}>
                                    <SelectTrigger className="w-full h-9 rounded-lg border border-border bg-background text-sm focus:ring-0 focus:ring-offset-0">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="z-[70]">
                                      {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-4 py-2 align-top">
                                  <Input 
                                    type="number"
                                    value={item.harga} 
                                    onChange={e => handleItemChange(item.id, 'harga', Number(e.target.value))} 
                                    className="h-9 rounded-lg"
                                  />
                                </td>
                                <td className="px-4 py-2 align-top">
                                  <Input 
                                    value={item.catatan} 
                                    onChange={e => handleItemChange(item.id, 'catatan', e.target.value)} 
                                    placeholder="Opsional"
                                    className="h-9 rounded-lg"
                                  />
                                </td>
                                <td className="px-4 py-2 align-top text-center">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={() => removeItem(item.id)}
                                    className="h-9 w-9 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Add manual row button */}
                    <div className="p-3 border-t border-border bg-muted/10">
                      <Button variant="ghost" onClick={() => setScannedItems(prev => [...prev, { id: Date.now(), nama_bibit: '', harga: 0, mata_uang: globalCurrency, catatan: '' }])} className="w-full mt-2 rounded-xl border border-dashed border-border text-muted-foreground hover:bg-muted/50">
                        + Tambah Baris Manual
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-5 sm:p-6 border-t border-border bg-muted/20 flex justify-end gap-3 mt-auto">
          <Button variant="outline" onClick={onClose} className="rounded-xl px-6">
            Batal
          </Button>
          <Button 
            onClick={handleSaveAll} 
            disabled={isProcessing || !imagePreview || saveMut.isPending || scannedItems.length === 0} 
            className="rounded-xl px-8 gap-2 bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all"
          >
            {saveMut.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Simpan Semua ({scannedItems.length} Item)
          </Button>
        </div>

      </div>
      
      {/* Fullscreen Image Preview */}
      {isFullscreen && imagePreview && (
        <div className="fixed inset-0 z-[70] bg-black/90  flex items-center justify-center p-4 cursor-pointer" onClick={() => setIsFullscreen(false)}>
          <img src={imagePreview} alt="Full Preview" className="max-w-full max-h-full object-contain rounded-xl" />
          <Button variant="ghost" size="icon" className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-rose-500 hover:text-white text-white transition-colors" onClick={() => setIsFullscreen(false)}>
            <X className="w-6 h-6" />
          </Button>
        </div>
      )}
    </div>
  );
}
